import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '@env';
import { CustomHttpParamEncoder } from '@shared/encoder';
import {
  AcceptanceDocument,
  AcceptanceDocumentResponse,
  AcceptanceDocumentsRequestParams,
  AcceptedDocumentList,
  AcceptedDocumentListRaw,
  ConsentEventList,
  ConsentEventListRaw,
  ConsentEventsRequestParams,
} from '@shared/models';
import { BackendService } from '@shared/services/backend.service';

import { acceptedDocumentName, mapConsentEvent } from './consent-event-mapping-utils';

@Injectable({
  providedIn: 'root',
})
export class FinancialAccountDocumentService {
  http = inject(HttpClient);

  constructor(private backendService: BackendService) {}

  public getFinancialAccountAcceptanceDocuments({
    accountHolderType,
    accountHolderSubtype,
  }: AcceptanceDocumentsRequestParams): Observable<AcceptanceDocument[]> {
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('accountHolderType', accountHolderType);

    if (accountHolderSubtype) {
      params = params.append('accountHolderSubtype', accountHolderSubtype);
    }

    return this.backendService
      .get<AcceptanceDocument[]>(`${environment.financialAccountFlow}/integrated-bank-accounts/acceptance-documents`, { params })
      .pipe(
        map((response) => response),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  public getAcceptedDocuments({ financialAccountId }: { financialAccountId: string }): Observable<AcceptedDocumentList> {
    const params = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('financialAccountId', financialAccountId);

    return this.backendService.get<AcceptedDocumentListRaw>(`${environment.documentAcceptanceService}/acceptances`, { params }).pipe(
      map((response) => {
        const { content = [], totalElements = 0 } = response;

        const items = content.map((item) => ({
          documentName: acceptedDocumentName(item.documentId),
          documentId: item.documentId,
          documentVersion: item.documentVersion,
          isAccepted: item.accepted ? 'Yes' : 'No',
          acceptedAt: item.respondedAt,
          downloadUrl: item.downloadUrl,
        }));

        return {
          items,
          totalElements,
        };
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getConsentEvents({ page, size, sort, ...filters }: ConsentEventsRequestParams): Observable<ConsentEventList> {
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });
    Object.entries({ ...filters, page, size, sort }).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return this.backendService
      .get<ConsentEventListRaw>(`${environment.documentAcceptanceService}/acceptances/consent-events`, { params })
      .pipe(
        map(({ content = [], ...paging }) => ({ ...paging, items: content.map(mapConsentEvent) })),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  public getAcceptanceDocument(documentUrl: string): Observable<AcceptanceDocumentResponse> {
    return this.http
      .get(documentUrl, {
        responseType: 'arraybuffer',
        observe: 'response',
      })
      .pipe(
        map((response) => response as AcceptanceDocumentResponse),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }
}
